import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { orderService, stockService } from "../services/apiService";
import { useProfile } from "../utils/UseProfile";
import { useLoader } from "../utils/LoaderContext";

const PAGE_SIZE = 15;

const SortIcon = ({ col, sortConfig }) => {
  if (!sortConfig || sortConfig.key !== col) return <i className="fas fa-sort text-gray-300 ml-1 text-xs" />;
  return sortConfig.dir === "asc" ? <i className="fas fa-sort-up text-indigo-500 ml-1 text-xs" /> : <i className="fas fa-sort-down text-indigo-500 ml-1 text-xs" />;
};

const Th = ({ col, label, className = "text-left", sortConfig, onSort }) => (
  <th className={`py-2 px-3 ${className} cursor-pointer select-none hover:bg-blue-100`} onClick={() => onSort(col)}>
    {label}<SortIcon col={col} sortConfig={sortConfig} />
  </th>
);

const ProductSalesReport = () => {
  const { showLoader, hideLoader } = useLoader();
  const { profile } = useProfile();
  const role = profile?.roleResponse?.role;
  const isAdmin = Array.isArray(role)
    ? role.some((r) => r.toUpperCase().includes("ADMIN"))
    : typeof role === "string" && role.toUpperCase().includes("ADMIN");

  const [showSize, setShowSize] = useState(false);

  useEffect(() => {
    stockService.getClientFieldConfig()
      .then((res) => {
        const sizeConfig = (res || []).find((f) => f.fieldName === "size");
        setShowSize(sizeConfig?.visible === true);
      })
      .catch(() => setShowSize(false));
  }, []);

  const [filters, setFilters] = useState({
    fromDate: "", toDate: "", productName: "", productType: "",
    manufacturer: "", customerMobile: "", batchNo: "",
  });
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const handleSort = (key) => setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedResults = [...results].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? "";
    const bVal = b[sortConfig.key] ?? "";
    const cmp = typeof aVal === "number" && typeof bVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  });

  const handleChange = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const fetchReport = async (pageNo = 0, override = null) => {
    showLoader();
    setLoading(true);
    setHasSearched(true);
    try {
      const f = override || filters;
      const params = { page: pageNo, size: PAGE_SIZE };
      if (f.fromDate) params.fromDate = f.fromDate;
      if (f.toDate) params.toDate = f.toDate;
      if ((f.productName || "").trim()) params.productName = f.productName.trim();
      if ((f.productType || "").trim()) params.productType = f.productType.trim();
      if ((f.manufacturer || "").trim()) params.manufacturer = f.manufacturer.trim();
      if ((f.customerMobile || "").trim()) params.customerMobile = f.customerMobile.trim();
      if ((f.batchNo || "").trim()) params.batchNo = f.batchNo.trim();

      const data = await orderService.searchProductSalesReport(params);
      const list = data.content ?? [];
      const tp = data.totalPages ?? 0;
      const te = data.totalElements ?? 0;
      setResults(list);
      setPage(pageNo);
      setTotalPages(tp);
      setTotalElements(te);
      if (!list.length) setToast({ type: "error", message: "No records found" });
      else setToast({ type: "success", message: `Found ${te} record(s)` });
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.message || "Failed to fetch report" });
      setResults([]);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReport(0);
  };

  // AI "open this page" handoff: prefill filters + run the existing search.
  useEffect(() => {
    let nav = null;
    try {
      const raw = sessionStorage.getItem("jashNav");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.page === "product_sales_report") nav = parsed;
      }
    } catch { nav = null; }
    if (!nav) return;
    try { sessionStorage.removeItem("jashNav"); } catch { /* noop */ }
    const p = nav.prefill || {};
    const merged = {
      fromDate: p.fromDate || "",
      toDate: p.toDate || "",
      productName: p.productName || "",
      productType: p.productType || "",
      manufacturer: p.manufacturer || "",
      customerMobile: p.customerMobile || "",
      batchNo: p.batchNo || "",
    };
    setFilters(merged);
    if (nav.autoSearch !== false) fetchReport(0, merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setFilters({ fromDate: "", toDate: "", productName: "", productType: "", manufacturer: "", customerMobile: "", batchNo: "" });
    setResults([]);
    setHasSearched(false);
    setPage(0);
    setTotalPages(0);
    setTotalElements(0);
    setToast(null);
  };

  const baseColCount = 11 + (showSize ? 1 : 0) + (isAdmin ? 1 : 0);

  const filterFields = [
    { label: "From Date", name: "fromDate", type: "date" },
    { label: "To Date", name: "toDate", type: "date" },
    { label: "Product Name", name: "productName", type: "text", placeholder: "Product name" },
    { label: "Product Type", name: "productType", type: "text", placeholder: "Product type" },
    { label: "Manufacturer", name: "manufacturer", type: "text", placeholder: "Manufacturer" },
    { label: "Customer Mobile", name: "customerMobile", type: "tel", placeholder: "Mobile no" },
    { label: "Batch No", name: "batchNo", type: "text", placeholder: "Batch no" },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Filters */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          🔍 Product Sales Report
        </h2>
        <div className="p-2">
          <form onSubmit={handleSearch}>
            <div className="grid grid-cols-4 gap-2">
              {filterFields.slice(0, 4).map(({ label, name, type, placeholder }) => (
                <div key={name}>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">{label}</label>
                  <input
                    type={type}
                    name={name}
                    value={filters[name]}
                    onChange={handleChange}
                    onKeyDown={type === "date" ? (e) => { if (e.key === "Backspace" || e.key === "Delete") { e.preventDefault(); setFilters((p) => ({ ...p, [name]: "" })); } } : undefined}
                    placeholder={placeholder}
                    className="w-44 px-3 py-0.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none cursor-text"
                  />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              {filterFields.slice(4).map(({ label, name, type, placeholder }) => (
                <div key={name}>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">{label}</label>
                  <input
                    type={type}
                    name={name}
                    value={filters[name]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    className="w-44 px-3 py-0.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none cursor-text"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
                <i className="fas fa-redo" /> Reset
              </button>
              <button type="submit" disabled={loading}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                {loading ? <><i className="fas fa-spinner fa-spin" /> Searching...</> : <><i className="fas fa-search" /> Search</>}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            📋 Results
            {totalElements > 0 && (
              <span className="text-xs font-normal text-blue-600 ml-2">({totalElements} record{totalElements !== 1 ? "s" : ""})</span>
            )}
          </h2>
        </div>
        <div className="overflow-x-auto flex-1">
          <table className="min-w-full text-sm text-gray-800 table-auto">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0">
              <tr>
                <Th col="billNo" label="Invoice" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="orderDate" label="Date" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="productName" label="Product" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="productType" label="Category" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="manufacturer" label="Manufacturer" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="batchNo" label="Batch" sortConfig={sortConfig} onSort={handleSort} />
                {showSize && <Th col="size" label="Size" className="text-right" sortConfig={sortConfig} onSort={handleSort} />}
                <Th col="quantity" label="Qty" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="purchaseRate" label="Purchase" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="sellingRate" label="Selling" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="discount" label="Discount" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                <Th col="gst" label="GST" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                {isAdmin && <Th col="profit" label="Profit" className="text-right" sortConfig={sortConfig} onSort={handleSort} />}
                <Th col="saleType" label="Sale Type" className="text-center" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {!hasSearched ? (
                <tr>
                  <td colSpan={baseColCount} className="py-10 text-center text-gray-400">
                    <i className="fas fa-search text-4xl mb-3 block" />
                    <p className="text-sm">Use the filters above to search product sales.</p>
                  </td>
                </tr>
              ) : results.length === 0 ? (
                <tr>
                  <td colSpan={baseColCount} className="py-10 text-center text-gray-400">
                    <i className="fas fa-inbox text-4xl mb-3 block" />
                    <p className="text-sm">No records found. Try different filters.</p>
                  </td>
                </tr>
              ) : (
                sortedResults.map((row, idx) => {
                  const isReturn = row.saleType === "RETURN";
                  return (
                  <tr key={idx} className={`border-t hover:bg-blue-50 transition-colors ${
                    isReturn ? "bg-red-50" : idx % 2 === 0 ? "bg-white" : "bg-blue-50"
                  }`}>
                    <td className="py-2 px-3 font-medium text-indigo-700 text-xs">{row.billNo || "—"}</td>
                    <td className="py-2 px-3 text-xs text-gray-500">{row.orderDate ? (() => { const [d, t] = row.orderDate.split(" "); const [day, mon, yr] = d.split("/"); return `${day}/${mon}/${yr}${t ? " " + t : ""}`; })() : "—"}</td>
                    <td className="py-2 px-3 text-xs">{row.productName || "—"}</td>
                    <td className="py-2 px-3 text-xs">{row.productType || "—"}</td>
                    <td className="py-2 px-3 text-xs">{row.manufacturer || "—"}</td>
                    <td className="py-2 px-3 text-xs">{row.batchNo || "—"}</td>
                    {showSize && <td className="py-2 px-3 text-right text-xs">{row.size ?? "—"}</td>}
                    <td className={`py-2 px-3 text-right font-semibold ${isReturn ? "text-red-600" : "text-gray-800"}`}>{row.quantity ?? "—"}</td>
                    <td className={`py-2 px-3 text-right text-xs ${isReturn ? "text-red-500" : ""}`}>{row.purchaseRate != null ? `₹${Number(row.purchaseRate).toFixed(2)}` : "—"}</td>
                    <td className={`py-2 px-3 text-right text-xs ${isReturn ? "text-red-500" : ""}`}>{row.sellingRate != null ? `₹${Number(row.sellingRate).toFixed(2)}` : "—"}</td>
                    <td className="py-2 px-3 text-right text-xs">{row.discount != null ? `${row.discount}%` : "—"}</td>
                    <td className="py-2 px-3 text-right text-xs">{row.gst != null ? `${row.gst}%` : "—"}</td>
                    {isAdmin && <td className={`py-2 px-3 text-right font-semibold text-xs ${isReturn ? "text-red-600" : "text-teal-700"}`}>{row.profit != null ? `₹${Number(row.profit).toFixed(2)}` : "—"}</td>}
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        isReturn ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                      }`}>{row.saleType || "SALE"}</span>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
            {isAdmin && results.length > 0 && (
              <tfoot>
                <tr className="bg-gradient-to-r from-teal-50 to-indigo-50 border-t-2 border-indigo-200 font-semibold text-xs">
                  <td colSpan={6 + (showSize ? 1 : 0)} className="py-2 px-3 text-right text-gray-600">Page Total</td>
                  <td className="py-2 px-3 text-right text-gray-700">{results.reduce((s, r) => s + (r.quantity != null ? Number(r.quantity) : 0), 0)}</td>
                  <td className="py-2 px-3 text-right text-gray-700">₹{results.reduce((s, r) => s + (r.purchaseRate != null ? Number(r.purchaseRate) : 0), 0).toFixed(2)}</td>
                  <td className="py-2 px-3 text-right text-blue-700">₹{results.reduce((s, r) => s + (r.sellingRate != null ? Number(r.sellingRate) : 0), 0).toFixed(2)}</td>
                  <td className="py-2 px-3" />
                  <td className="py-2 px-3" />
                  <td className="py-2 px-3 text-right text-teal-700">₹{results.reduce((s, r) => s + (r.profit != null ? Number(r.profit) : 0), 0).toFixed(2)}</td>
                  <td className="py-2 px-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
          <span className="text-xs text-gray-500">
            {totalElements > 0 ? `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, totalElements)} of ${totalElements}` : ""}
          </span>
          {totalElements > PAGE_SIZE && (
            <div className="flex items-center gap-1">
              <button onClick={() => fetchReport(0)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => fetchReport(page - 1)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i)
                .slice(Math.max(0, page - 2), page + 3)
                .map((i) => (
                  <button key={i} onClick={() => fetchReport(i)}
                    className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>
                    {i + 1}
                  </button>
                ))}
              <button onClick={() => fetchReport(page + 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => fetchReport(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductSalesReport;
